<template>
    <div class="flex flex-col gap-4 p-4 sm:p-6">
        <div v-if="loading && !loaded" class="flex justify-center py-8">
            <UIcon name="i-lucide-loader-circle" class="size-5 animate-spin text-muted" />
        </div>

        <p v-else-if="comments.length === 0" class="py-4 text-center text-sm text-muted">
            {{ $t('comment.empty') }}
        </p>

        <ul v-else class="flex flex-col gap-4">
            <li v-for="comment in comments" :key="comment.id" class="flex gap-3">
                <UAvatar :alt="comment.author.name" size="sm" class="shrink-0" />

                <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-baseline gap-x-2">
                        <span class="text-sm font-medium text-highlighted">{{ comment.author.name }}</span>
                        <span class="text-xs text-dimmed" :title="formatDateTime(comment.created_at)">{{ agoLabel(comment.created_at) }}</span>
                        <span v-if="comment.edited_at" class="text-xs text-dimmed" :title="formatDateTime(comment.edited_at)">
                            · {{ $t('comment.edited') }}
                        </span>

                        <div v-if="editingId !== comment.id && (comment.can_edit || comment.can_delete)" class="ml-auto flex gap-0.5">
                            <UButton
                                v-if="comment.can_edit"
                                icon="i-lucide-pencil"
                                color="neutral"
                                variant="ghost"
                                size="xs"
                                :aria-label="$t('general.edit')"
                                @click="startEdit(comment)"
                            />
                            <UButton
                                v-if="comment.can_delete"
                                icon="i-lucide-trash-2"
                                color="error"
                                variant="ghost"
                                size="xs"
                                :aria-label="$t('general.delete')"
                                @click="remove(comment)"
                            />
                        </div>
                    </div>

                    <UForm
                        v-if="editingId === comment.id"
                        :state="editForm"
                        :schema="schema"
                        class="mt-1 flex flex-col gap-2"
                        @submit="saveEdit(comment)"
                    >
                        <UFormField name="body">
                            <UTextarea
                                v-model="editForm.body"
                                autoresize
                                :rows="2"
                                :maxrows="12"
                                :maxlength="TaskLimits.Comment"
                                autofocus
                                class="w-full"
                                @keydown.meta.enter.prevent="saveEdit(comment)"
                                @keydown.ctrl.enter.prevent="saveEdit(comment)"
                            />
                        </UFormField>
                        <div class="flex justify-end gap-2">
                            <UButton :label="$t('general.cancel')" color="neutral" variant="subtle" size="sm" @click="editingId = ''" />
                            <UButton :label="$t('general.save')" type="submit" size="sm" :loading="appStore.isLoaderActive()" />
                        </div>
                    </UForm>

                    <p v-else class="mt-0.5 whitespace-pre-wrap break-words text-sm text-default">{{ comment.body }}</p>
                </div>
            </li>
        </ul>

        <UForm :state="newForm" :schema="schema" class="flex flex-col gap-2 border-t border-default pt-4" @submit="add">
            <UFormField name="body">
                <UTextarea
                    v-model="newForm.body"
                    autoresize
                    :rows="2"
                    :maxrows="12"
                    :maxlength="TaskLimits.Comment"
                    :placeholder="$t('comment.placeholder')"
                    class="w-full"
                    @keydown.meta.enter.prevent="add"
                    @keydown.ctrl.enter.prevent="add"
                />
            </UFormField>
            <div class="flex items-center justify-between gap-2">
                <span class="hidden text-xs text-dimmed sm:inline">{{ $t('comment.shortcut') }}</span>
                <UButton :label="$t('comment.add')" type="submit" icon="i-lucide-send" size="sm" :loading="appStore.isLoaderActive()" class="ml-auto" />
            </div>
        </UForm>
    </div>

    <AppConfirmDialog ref="confirmDialog" />
</template>

<script lang="ts" setup>
    import { computed, onMounted, reactive, ref, useTemplateRef } from 'vue'
    import { useI18n } from 'vue-i18n'
    import * as z from 'zod'

    import AppConfirmDialog from '@/components/app/AppConfirmDialog.vue'
    import { TaskLimits } from '@/enums/task'
    import { agoLabel, formatDateTime } from '@/libs/dayjs'
    import { parseKnownError } from '@/libs/error'
    import { PrintError, PrintSuccess } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { IComment } from '@/types/Task'

    /*
     * The conversation on a task, oldest first. Anybody who can see the task can write here;
     * a comment is edited only by its author and deleted by its author or whoever can edit
     * the project — the API says which on every comment (`can_edit`, `can_delete`).
     */
    const props = defineProps<{ taskId: string }>()

    const emit = defineEmits<((e: 'changed', count: number) => void)>()

    const { t }         = useI18n()
    const appStore      = useAppStore()
    const fetchStore    = useFetchStore()
    const confirmDialog = useTemplateRef<InstanceType<typeof AppConfirmDialog>>('confirmDialog')

    const comments  = ref<IComment[]>([])
    const loading   = ref(false)
    const loaded    = ref(false)
    const editingId = ref('')
    const newForm   = reactive({ body: '' })
    const editForm  = reactive({ body: '' })

    const schema = computed(() => z.object({
        body: z.string().trim().min(1, t('validation.required')).max(TaskLimits.Comment, t('validation.too_long')),
    }))

    const load = async () => {
        loading.value = true

        const { statusCode, data } = await fetchStore.fetch(`/tasks/${props.taskId}/comments`).get().json<IComment[]>()

        if (statusCode.value === 200) {
            comments.value = data.value ?? []
            loaded.value   = true
        } else {
            PrintError(parseKnownError(data.value, t('comment.load_failed')))
        }

        loading.value = false
    }

    const reloadAndTell = async () => {
        await load()
        emit('changed', comments.value.length)
    }

    const add = async () => {
        const body = newForm.body.trim()
        if (!body) return

        appStore.startLoading()

        const { statusCode, data } = await fetchStore.fetch(`/tasks/${props.taskId}/comments`).post({ body }).json<{ id: string }>()

        appStore.stopLoading()

        if (statusCode.value === 201) {
            newForm.body = ''
            await reloadAndTell()
        } else {
            PrintError(parseKnownError(data.value, t('comment.add_failed')))
        }
    }

    const startEdit = (comment: IComment) => {
        editingId.value = comment.id
        editForm.body   = comment.body
    }

    const saveEdit = async (comment: IComment) => {
        const body = editForm.body.trim()
        if (!body) return

        appStore.startLoading()

        const { statusCode, data } = await fetchStore.fetch(`/comments/${comment.id}`).put({ body }).json()

        appStore.stopLoading()

        if (statusCode.value === 200) {
            editingId.value = ''
            await load()
        } else {
            PrintError(parseKnownError(data.value, t('comment.edit_failed')))
        }
    }

    const remove = async (comment: IComment) => {
        const confirmed = await confirmDialog.value?.confirm(t('comment.confirm_delete'), {
            title:        t('comment.delete'),
            confirmLabel: t('general.delete'),
        })
        if (!confirmed) return

        appStore.startLoading()

        const { statusCode, data } = await fetchStore.fetch(`/comments/${comment.id}`).delete().json()

        appStore.stopLoading()

        if (statusCode.value === 204) {
            PrintSuccess(t('comment.deleted'))
            await reloadAndTell()
        } else {
            PrintError(parseKnownError(data.value, t('comment.delete_failed')))
        }
    }

    onMounted(load)
</script>
